import {
  CoverageCalculator,
  CoverageCalculatorOptions,
  CoveragePriceResult,
  CustomerInfo,
  GetCoverageParams,
  GetCoverageResult,
  getItemEligibilityInfoParams,
  isProductEnabledParams,
  ItemInfo,
} from ".";
import { DynamicReturnTypes, InternationalReturnPricing } from "../team";
import {
  getReturnsCoverageExclusions,
  itemValidForReturnsInfo,
} from "./check-product";
import {
  getAllReturnEligibleCountries,
  getInternationalPrice,
} from "./country-check";
import { calculateDynamicReturnsPrice } from "./utils/calculate-dynamic-returns-price";
import { getPricingRulesInLocalCurrency } from "./utils/get-pricing-rules-in-local-currency";
import { listsHaveMatchingElement } from "./utils/lists-have-matching-element";

const isProductEnabled = ({
  team,
  ignoreMerchantPaidForEnablementCheck,
}: isProductEnabledParams) => {
  return !!(
    team.settings.enabled &&
    team.settings.returns?.enabled &&
    team.settings.coverageEnabled &&
    (ignoreMerchantPaidForEnablementCheck
      ? true
      : team.settings.paidModel === "customer_paid") &&
    (team.pricePerOrder || team.settings.returns.dynamicPricing)
  );
};

const getShopperEligibilityInfo = (
  customerInfo: CustomerInfo,
  team: CoverageCalculatorOptions,
) => {
  if (
    listsHaveMatchingElement(
      customerInfo.customerTags,
      team.settings.coverageExcludeCustomerTags,
    )
  ) {
    return { isEligible: false, reasonIneligible: "customer_tags" };
  }

  const returnCountries = getAllReturnEligibleCountries(
    team.settings.countries,
    team.settings.returns?.internationalPricing,
  );
  const ValidCountry =
    returnCountries.includes(customerInfo.country) ||
    returnCountries.length == 0;

  if (!ValidCountry) {
    return {
      isEligible: false,
      reasonIneligible: "shopper is in an ineligible country",
    };
  }

  if (
    customerInfo.isBusinessAccount &&
    team.settings.shopifyExtension?.disableBusinessAccountInStorefront
  ) {
    return { isEligible: false, reasonIneligible: "business account excluded" };
  }

  if (customerInfo.usingZapietPickup) {
    return { isEligible: false, reasonIneligible: "zapiet pickup excluded" };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

function isItemPastCouponThreshold(
  price: number,
  preCouponPrice: number,
  couponedItemsDiscountThreshold: number | null | undefined,
): boolean {
  if (!couponedItemsDiscountThreshold) {
    return false;
  }
  if (preCouponPrice === 0) {
    return false;
  }
  const threshold = couponedItemsDiscountThreshold * 0.01;

  return (preCouponPrice - price) / preCouponPrice > threshold;
}

const getItemEligibilityInfo = ({
  item,
  team,
  commentsoldIntegration,
}: getItemEligibilityInfoParams) => {
  if (item.vendor === "re:do") {
    return { isEligible: false, reasonIneligible: "redo product excluded" };
  }

  if (item.isFinalSale) {
    return { isEligible: false, reasonIneligible: "item marked as final sale" };
  }

  if (item.requiresShipping === false) {
    return {
      isEligible: false,
      reasonIneligible: "item does not require shipping",
    };
  }

  if (item.price && team.settings.coverageExcludeCentEnding.length) {
    const cents = item.price.toFixed(2).split(".")[1];
    const centExclusions = team.settings.coverageExcludeCentEnding;
    const exclusionMatch = centExclusions.find(
      (centExclusion) => cents === centExclusion,
    );

    if (exclusionMatch) {
      return { isEligible: false, reasonIneligible: "excluded cent ending" };
    }
  }

  // This handles the case where the discount only has a name and not a code
  const hasExcludedDiscount = item.discounts.some((code) =>
    team.settings.discountCodePartialMatch
      ? team.settings.coverageExcludeDiscountCodes.some((exclude) =>
          (code.code || code.name).includes(exclude),
        )
      : team.settings.coverageExcludeDiscountCodes.includes(
          code.code || code.name,
        ),
  );
  if (hasExcludedDiscount) {
    return { isEligible: false, reasonIneligible: "discount code" };
  }

  // this settings is misnamed. it actually means products with a compare at price is ineligible.
  if (
    team.settings.publicapi?.discountsAreFinalSales &&
    item.compareAtPrice &&
    item.preDiscountPrice < item.compareAtPrice
  ) {
    return {
      isEligible: false,
      reasonIneligible: "Item is discounted and all discounts are final sales",
    };
  }

  if (
    isItemPastCouponThreshold(
      item.price,
      item.preDiscountPrice,
      team.settings.publicapi?.couponedItemsDiscountThreshold,
    )
  ) {
    return {
      isEligible: false,
      reasonIneligible: "Item is discounted and past the coupon threshold",
    };
  }

  const coverageExclusions = getReturnsCoverageExclusions({
    returnsCoverage: {
      coverageExcludeProperties: team.settings.coverageExcludeProperties,
      coverageExcludeCollections: team.settings.coverageExcludeCollections,
      invalidProductTags: team.settings.coverageExcludeProductTags,
    },
    finalSaleReturnsCoverage: {
      coverageEnabled:
        !!team.settings.returns?.finalSaleReturns?.enabled &&
        commentsoldIntegration?.returnsSettings?.enableFinalSaleCoverage !==
          false,
      validProductTags:
        team.settings.returns?.finalSaleReturns?.validProductTags ?? [],
      validCollectionTitles:
        team.settings.returns?.finalSaleReturns?.validCollectionTitles ?? [],
    },
  });

  const isItemValidInfo = itemValidForReturnsInfo({
    ...coverageExclusions,
    itemInfo: item,
  });
  if (!isItemValidInfo.isEligible) {
    return {
      isEligible: false,
      reasonIneligible: isItemValidInfo.reasonIneligible,
    };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

const calculateCoveragePrice = ({
  team,
  country,
  numProducts,
  amount,
  conversionRate,
}: {
  team: CoverageCalculatorOptions;
  country: string | undefined;
  numProducts: number;
  amount: number;
  conversionRate?: number;
}): CoveragePriceResult => {
  const dynamicPricing = team.settings.returns?.dynamicPricing;

  if (conversionRate) {
    if (
      dynamicPricing &&
      dynamicPricing.type === DynamicReturnTypes.ITEM_COUNT
    ) {
      dynamicPricing.maxPrice = dynamicPricing.maxPrice * conversionRate;
      dynamicPricing.pricePerAdditonalItem =
        dynamicPricing.pricePerAdditonalItem * conversionRate;
    } else if (
      dynamicPricing &&
      dynamicPricing.type === DynamicReturnTypes.CART_VALUE
    ) {
      dynamicPricing.maxPrice = dynamicPricing.maxPrice * conversionRate;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      dynamicPricing.pricingRuleSet = getPricingRulesInLocalCurrency(
        [dynamicPricing.pricingRuleSet],
        conversionRate,
      )[0];
    }

    if (team.pricePerOrder) {
      team.pricePerOrder = (
        parseFloat(team.pricePerOrder) * conversionRate
      ).toString();
    }
  }

  const basePrice = parseFloat(team.pricePerOrder || "0");
  if (isNaN(basePrice) || !basePrice) {
    return { price: 0, source: "unspecified" };
  }
  let coveragePrice = basePrice;

  if (dynamicPricing) {
    coveragePrice = calculateDynamicReturnsPrice(
      dynamicPricing,
      basePrice,
      numProducts,
      amount,
    ).price;
  }

  if (team.settings.returns?.internationalPricing && country) {
    coveragePrice = getInternationalPrice(
      coveragePrice,
      country,
      team.settings.countries,
      team.settings.returns.internationalPricing as InternationalReturnPricing,
    );
  }

  return { price: coveragePrice, source: "unspecified" };
};

const getCartEligibilityInfo = (
  numValidItems: number,
  numInvalidItems: number,
  cartTotal: number,
  team: CoverageCalculatorOptions,
) => {
  if (team.settings.excludeCoverageIfAnyInvalidProduct && numInvalidItems > 0) {
    return {
      isEligible: false,
      reasonIneligible: "cart has at least one invalid product",
    };
  }

  return { isEligible: numValidItems > 0, reasonIneligible: undefined };
};

// returns a price if coverage is valid. otherwise returns undefined
const getCoverage = ({
  team,
  customerInfo,
  items,
  conversionRate,
  ignoreMerchantPaidForEnablementCheck,
  commentsoldIntegration,
}: GetCoverageParams): GetCoverageResult => {
  if (
    !isProductEnabled({
      team,
      ignoreMerchantPaidForEnablementCheck,
      commentsoldIntegration,
    })
  ) {
    return {
      eligible: false,
      priceResult: undefined,
      reasonIneligible: "product not enabled",
      eligibleItems: [],
    };
  }

  const shopperEligibleInfo = getShopperEligibilityInfo(customerInfo, team);
  if (!shopperEligibleInfo.isEligible) {
    return {
      eligible: false,
      priceResult: undefined,
      reasonIneligible: shopperEligibleInfo.reasonIneligible,
      eligibleItems: [],
    };
  }

  let totalPrice = 0;
  let numValidItems = 0;
  let numInvalidItems = 0;
  const eligibleItems: ItemInfo[] = [];

  const ineligibleItemReasons = [];

  for (const item of items) {
    const itemEligibleInfo = getItemEligibilityInfo({
      item,
      team,
      commentsoldIntegration,
    });
    if (itemEligibleInfo.isEligible) {
      totalPrice += item.price * item.quantity;
      numValidItems += item.quantity;
      eligibleItems.push(item);
    } else {
      numInvalidItems += item.quantity;
      ineligibleItemReasons.push(itemEligibleInfo.reasonIneligible);
    }
  }

  const cartEligibleInfo = getCartEligibilityInfo(
    numValidItems,
    numInvalidItems,
    totalPrice,
    team,
  );
  if (!cartEligibleInfo.isEligible) {
    return {
      eligible: false,
      priceResult: undefined,
      reasonIneligible:
        cartEligibleInfo.reasonIneligible || ineligibleItemReasons.join(", "),
      eligibleItems: [],
    };
  }

  const priceResult = calculateCoveragePrice({
    team,
    country: customerInfo.country,
    numProducts: numValidItems,
    amount: totalPrice,
    conversionRate,
  });

  return {
    eligible: true,
    priceResult,
    reasonIneligible: undefined,
    eligibleItems,
  };
};

export const returnCoverageCalculator: CoverageCalculator = {
  type: "Returns",
  isProductEnabled,
  getShopperEligibilityInfo,
  getItemEligibilityInfo,
  getCartEligibilityInfo,
  // need to handle non usd here.
  calculateCoveragePrice,
  getCoverage,
};
